import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMultipleUsersAsync,
  selectMultipleUsers,
  selectIsLoadingMultiple,
} from "@/store/redux/slices/user/user_slice";

const ScheduleCard = ({ schedule }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: eventId } = useParams();
  const [memberUsers, setMemberUsers] = useState([]);
  const fetchedUsers = useSelector(selectMultipleUsers);
  const isLoadingUsers = useSelector(selectIsLoadingMultiple);

  useEffect(() => {
    // Fetch members data if there are members
    if (schedule.members && schedule.members.length > 0) {
      dispatch(fetchMultipleUsersAsync(schedule.members));
    }
  }, [schedule.members, dispatch]);

  useEffect(() => {
    if (fetchedUsers && fetchedUsers.length > 0 && schedule.members) {
      // Filter fetched users to only those in this schedule
      const relevantUsers = fetchedUsers.filter((user) =>
        schedule.members.includes(user._id)
      );
      setMemberUsers(relevantUsers);
    }
  }, [fetchedUsers, schedule.members]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const subEventId = schedule._id;

    if (subEventId) {
      const path = `/dashboard/events/${eventId}/schedule/${subEventId}`;
      navigate(path, {
        replace: true,
        state: { parentEventName: schedule.eventName }, // Pass event name for breadcrumb
      });
    } else {
      console.error("No _id found in schedule object:", schedule);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="mb-4 pt-[10px] pl-[20px] pb-[10px] pr-[20px] rounded-lg h-[129px] border border-[#E1F0FF] cursor-pointer hover:shadow-md transition-shadow"
      style={{
        background: "linear-gradient(to right, #E1F0FF, #FFFFFF)",
      }}>
      <div className="flex flex-col items-start">
        <h3 className="preserve-text-black text-bold-x">{schedule.title}</h3>
        <p className="preserve-text-black text-small">{schedule.address}</p>
        <p className="preserve-text-black text-small">{schedule.location}</p>
        <p className="preserve-text-black text-small">{schedule.timeRange}</p>

        <div className="flex items-center mt-3">
          {schedule.members && schedule.members.length > 0 ? (
            <Avatar.Group
              maxCount={4}
              maxStyle={{ color: "#f56a00", backgroundColor: "#fde3cf" }}>
              {isLoadingUsers ? (
                // Show loading avatars while fetching
                [1, 2, 3].map((i) => (
                  <Avatar key={i} style={{ backgroundColor: "#f0f0f0" }} />
                ))
              ) : memberUsers.length > 0 ? (
                // Show actual user avatars when data is available
                memberUsers.map((user) => (
                  <Tooltip key={user._id} title={user.fullname || "User"}>
                    <Avatar
                      src={user.profileImage}
                      style={{
                        backgroundColor: user.profileImage
                          ? "transparent"
                          : "#1677ff",
                      }}>
                      {!user.profileImage &&
                        (user.fullname
                          ? user.fullname.charAt(0).toUpperCase()
                          : "U")}
                    </Avatar>
                  </Tooltip>
                ))
              ) : (
                // Fallback if no members are fetched
                <Avatar style={{ backgroundColor: "#1677ff" }}>U</Avatar>
              )}
            </Avatar.Group>
          ) : (
            // Show a placeholder if no members are assigned
            <p className="text-xs text-gray-500">No members assigned</p>
          )}
        </div>
      </div>
    </div>
  );
};

ScheduleCard.propTypes = {
  eventName: PropTypes.string,
  schedule: PropTypes.shape({
    _id: PropTypes.string,
    eventName: PropTypes.string,
    title: PropTypes.string,
    address: PropTypes.string,
    location: PropTypes.string,
    timeRange: PropTypes.string,
    members: PropTypes.array,
  }),
};

export default ScheduleCard;
